/* Timbrica — /satellite-tracker (live satellites + ISS pass predictor + Starlink).
 *
 * TLEs come from our same-origin CelesTrak proxy (RU-safe); positions are
 * propagated locally with satellite.js (SGP4) and drawn on a Leaflet canvas.
 * Differentiators vs the 3D globe's single ISS dot: a whole constellation, a
 * Starlink mode, and a location-based VISIBLE-pass predictor that does the real
 * sunlit-satellite + dark-observer geometry.
 */
(function () {
  'use strict';
  var C = window.SAT_CFG || {};
  var T = C.t || {}, API = C.api || {};
  var tr = function (k) { return T[k] != null ? T[k] : k; };
  var $ = function (id) { return document.getElementById(id); };
  var ae = function (ev, p) { try { window.__ae && window.__ae(ev, p || {}); } catch (e) {} };
  var rad = Math.PI / 180, RE = 6378.137;

  // ── sun geometry (for the sunlit-satellite + dark-observer pass test) ──────
  function toDays(d) { return d.valueOf() / 864e5 - 0.5 + 2440588 - 2451545; }
  function sunLon(d) { var M = rad * (357.5291 + 0.98560028 * d); return M + rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M)) + rad * 102.9372 + Math.PI; }
  function sunEci(date) { var L = sunLon(toDays(date)), e = rad * 23.4397; return [Math.cos(L), Math.cos(e) * Math.sin(L), Math.sin(e) * Math.sin(L)]; }
  function sunAltDeg(date, lat, lng) {
    var d = toDays(date), L = sunLon(d), e = rad * 23.4397;
    var dec = Math.asin(Math.sin(e) * Math.sin(L));
    var ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L));
    var gmst = rad * (280.16 + 360.9856235 * d), H = gmst - rad * -lng - ra, phi = rad * lat;
    return Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H)) / rad;
  }
  function sunlit(pos, date) {
    var s = sunEci(date), dot = pos.x * s[0] + pos.y * s[1] + pos.z * s[2];
    if (dot > 0) return true;
    var px = pos.x - dot * s[0], py = pos.y - dot * s[1], pz = pos.z - dot * s[2];
    return Math.sqrt(px * px + py * py + pz * pz) > RE;
  }

  // ── TLE parse + propagation ────────────────────────────────────────────────
  function parseTLE(text) {
    var out = [], lines = text.split(/\r?\n/);
    for (var i = 0; i + 2 < lines.length + 1; i++) {
      if (lines[i] && lines[i][0] !== '1' && lines[i][0] !== '2' && lines[i + 1] && lines[i + 1][0] === '1' && lines[i + 2] && lines[i + 2][0] === '2') {
        try {
          var rec = satellite.twoline2satrec(lines[i + 1], lines[i + 2]);
          out.push({ name: lines[i].trim(), rec: rec, norad: lines[i + 1].substring(2, 7).trim() });
        } catch (e) {}
        i += 2;
      }
    }
    return out;
  }
  function propagate(rec, date) {
    var pv = satellite.propagate(rec, date);
    if (!pv || !pv.position) return null;
    var gmst = satellite.gstime(date), gd = satellite.eciToGeodetic(pv.position, gmst);
    var v = pv.velocity, spd = v ? Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) : 0;
    return { lat: satellite.degreesLat(gd.latitude), lon: satellite.degreesLong(gd.longitude), alt: gd.height, spd: spd, eci: pv.position };
  }
  function orbitClass(alt) { return alt < 2000 ? 'leo' : alt < 30000 ? 'meo' : 'geo'; }
  /* Две палитры орбит. Холодная синь #7fd1ff и жёлтый #ffd24d сделаны под
     чёрное небо; на светлой подложке (positron, #fafaf8) они выцветают —
     низкие орбиты, которых больше всего, исчезают первыми. Контраст светлой
     тройки к светлой подложке держит MapBasemapBrightnessTest (>3). */
  var RAMP = {
    dark:  { c: ['#7fd1ff', '#ffd24d', '#ff9f43'], iss: '#fff',     ring: '#36d399', trail: '#5ad1c8', ink: '#cfe0ff', sel: '#fff' },
    light: { c: ['#1565c0', '#9c7000', '#c2610a'], iss: '#0b1020', ring: '#0f7a4a', trail: '#0f7a72', ink: '#22304a', sel: '#0b1020' }
  };
  var skin = RAMP.dark;
  function orbitColor(alt) { return alt < 2000 ? skin.c[0] : alt < 30000 ? skin.c[1] : skin.c[2]; }

  // ── map ────────────────────────────────────────────────────────────────────
  var map = null, cv = null, sats = [], starlink = [], showStar = false, sel = null, userLL = null, iss = null;
  /* Держим ту библиотеку, которую ПРОВЕРИЛИ.
   *
   * Sentry H5: `Cannot read properties of undefined (reading 'setPosition')` в
   * `draw()` — то есть `L.DomUtil` оказался undefined, при живом `map`. Значит
   * при инициализации Leaflet был (иначе карта бы не построилась), а к моменту
   * отрисовки глобаль `L` уже не та. Тул открывали во встроенном браузере
   * приложения (Android WebView), а они внедряют в страницу свои скрипты.
   *
   * Цикл отрисовки идёт каждый кадр, поэтому чужая правка глобали превращается
   * в бесконечный поток отказов: 5 событий от ОДНОГО человека. Ссылку берём
   * один раз — там, где её уже проверили, — и дальше пользуемся ею. */
  var LF = null;

  function initMap() {
    if (!window.L || !$('stMap')) return;
    LF = window.L;
    var tile = C.tile || {};
    map = LF.map('stMap', { center: [C.home ? C.home.lat : 20, C.home ? C.home.lon : 10], zoom: C.home ? C.home.z : 2, minZoom: 1, maxZoom: 10, worldCopyJump: true });
    // Подложка через общий примитив: второй адрес (наш прокси <-> прямой CDN)
    // и честная строка, если не ответил ни один. Здесь стоял пустой
    // .on('tileerror', function () {}) — крючок был, внутри ничего, и
    // упавший хост давал ЧЁРНЫЙ прямоугольник без слова объяснения.
    if (window.__mapTiles) {
      window.__mapTiles.base(L, map, null, {
        slug: 'satellite-tracker', credit: tr('map_attr'), opts: { maxZoom: 19 }, text: tile.offline,
        styles: tile.styles, labels: tile.labels,
        onStyle: function (mode) {
          skin = RAMP[mode] || RAMP.dark;
          var st = document.getElementById('stStage');
          if (st) st.setAttribute('data-basemap', mode);
          if (typeof draw === 'function') draw();
        }
      });
    } else if (window.__basemapFallback) {
      window.__basemapFallback(LF, map, tr('map_attr'));
    }
    cv = document.createElement('canvas'); cv.className = 'st-canvas'; cv.style.pointerEvents = 'none';
    map.getPanes().overlayPane.appendChild(cv);
    map.on('move zoom resize viewreset moveend zoomend', draw);
    map.on('click', onClick);
  }

  function draw() {
    // Библиотека — такое же условие отрисовки, как холст и карта.
    if (!map || !cv || !LF || !LF.DomUtil) return;
    var size = map.getSize(), dpr = window.devicePixelRatio || 1, now = new Date();
    cv.width = size.x * dpr; cv.height = size.y * dpr; cv.style.width = size.x + 'px'; cv.style.height = size.y + 'px';
    LF.DomUtil.setPosition(cv, map.containerPointToLayerPoint([0, 0]));
    var ctx = cv.getContext('2d'); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, size.x, size.y);

    if (showStar) {
      ctx.fillStyle = skin.trail;
      for (var j = 0; j < starlink.length; j++) {
        var sp = starlink[j].pos; if (!sp) continue;
        var pp = map.latLngToContainerPoint([sp.lat, sp.lon]);
        if (pp.x < 0 || pp.y < 0 || pp.x > size.x || pp.y > size.y) continue;
        ctx.globalAlpha = 0.7; ctx.fillRect(pp.x - 1, pp.y - 1, 2, 2);
      }
      ctx.globalAlpha = 1;
    }
    for (var i = 0; i < sats.length; i++) {
      var s = sats[i], p = s.pos; if (!p) continue;
      var pt = map.latLngToContainerPoint([p.lat, p.lon]);
      if (pt.x < -8 || pt.y < -8 || pt.x > size.x + 8 || pt.y > size.y + 8) continue;
      var isIss = s === iss;
      ctx.beginPath(); ctx.arc(pt.x, pt.y, isIss ? 5 : (s === sel ? 4 : 2.4), 0, 6.2832);
      ctx.fillStyle = isIss ? skin.iss : orbitColor(p.alt); ctx.globalAlpha = isIss ? 1 : 0.85; ctx.fill();
      if (isIss || s === sel) { ctx.globalAlpha = 1; ctx.lineWidth = 1.5; ctx.strokeStyle = isIss ? skin.ring : skin.sel; ctx.stroke(); }
      if (isIss) { ctx.fillStyle = skin.ink; ctx.globalAlpha = .95; ctx.font = 'bold 11px system-ui'; ctx.fillText('ISS', pt.x + 8, pt.y + 4); }
    }
    ctx.globalAlpha = 1;
  }

  function tick() {
    var now = new Date();
    for (var i = 0; i < sats.length; i++) sats[i].pos = propagate(sats[i].rec, now);
    if (showStar) for (var j = 0; j < starlink.length; j++) starlink[j].pos = propagate(starlink[j].rec, now);
    draw();
  }

  function onClick(ev) {
    var c = ev.containerPoint, best = null, bd = 14;
    for (var i = 0; i < sats.length; i++) {
      var s = sats[i]; if (!s.pos) continue;
      var pt = map.latLngToContainerPoint([s.pos.lat, s.pos.lon]);
      var d = Math.hypot(pt.x - c.x, pt.y - c.y); if (d < bd) { bd = d; best = s; }
    }
    if (best) selectSat(best);
  }
  // Подпись точки — общий модуль GeoCity (public/js/geo-city.js): город на
  // языке страницы, румб и расстояние («↗ Город · 42 км»).
  function geoFill(lat, lon, elId, ok) {
    if (!window.GeoCity) return;
    GeoCity.fill(elId, lat, lon, { empty: '—', ok: ok });
  }

  function selectSat(s) {
    sel = s; draw();
    var card = $('stCard'); if (!card || !s.pos) return;
    card.innerHTML =
      '<button class="st-x" id="stX">&times;</button>' +
      '<div class="st-name">' + s.name + '</div>' +
      '<div class="st-row"><span>' + tr('altitude') + '</span><b>' + Math.round(s.pos.alt) + ' ' + tr('km') + '</b></div>' +
      '<div class="st-row"><span>' + tr('velocity') + '</span><b>' + s.pos.spd.toFixed(2) + ' ' + tr('kms') + '</b></div>' +
      '<div class="st-row"><span>' + tr('type') + '</span><b>' + tr('orbit_' + orbitClass(s.pos.alt)) + '</b></div>' +
      '<div class="st-row"><span>NORAD</span><b>' + s.norad + '</b></div>' +
      '<div class="st-row"><span>📍</span><b id="stGeo">…</b></div>';
    card.style.display = 'block';
    var x = $('stX'); if (x) x.onclick = function () { card.style.display = 'none'; sel = null; draw(); };
    geoFill(s.pos.lat, s.pos.lon, 'stGeo', function () { return sel === s; }); // satellite ground point at select time
    ae('feature_use', { slug: 'satellite-tracker', feature: 'select' });
  }

  // ── ISS visible-pass predictor ─────────────────────────────────────────────
  function predict(rec, lat, lon) {
    var gd = { longitude: lon * rad, latitude: lat * rad, height: 0.12 };
    var passes = [], inP = false, cur = null, step = 30000, t0 = Date.now();
    for (var t = t0; t < t0 + 72 * 3600e3 && passes.length < 5; t += step) {
      var date = new Date(t), pv = satellite.propagate(rec, date);
      if (!pv || !pv.position) continue;
      var gmst = satellite.gstime(date), ecf = satellite.eciToEcf(pv.position, gmst);
      var look = satellite.ecfToLookAngles(gd, ecf), el = look.elevation / rad;
      if (el > 10) {
        if (!inP) { inP = true; cur = { t: t, maxEl: 0, az: 0, vis: false }; }
        if (el > cur.maxEl) { cur.maxEl = el; cur.az = look.azimuth / rad; }
        if (sunlit(pv.position, date) && sunAltDeg(date, lat, lon) < -6) cur.vis = true;
      } else if (inP) {
        inP = false;
        if (cur.vis && cur.maxEl > 15) passes.push(cur);
        cur = null;
      }
    }
    return passes;
  }
  function compass(az) { var c = tr('compass').split(','); return c[Math.round(az / 45) % 8]; }
  function renderPasses(lat, lon) {
    var box = $('stPasses'); if (!box || !iss) return;
    var ps = predict(iss.rec, lat, lon);
    var html = '<div class="st-passes-h">' + tr('passes_title') + '</div>';
    if (!ps.length) { html += '<div class="st-pass-none">' + tr('no_passes') + '</div>'; }
    else {
      ps.forEach(function (p) {
        var d = new Date(p.t);
        var when = d.toLocaleString(C.locale, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
        html += '<div class="st-pass"><b>' + when + '</b><span>' + tr('pass_max') + ' ' + Math.round(p.maxEl) + '° · ' + compass(p.az) + '</span></div>';
      });
    }
    box.innerHTML = html; box.style.display = 'block';
  }

  // ── data load ──────────────────────────────────────────────────────────────
  function loadMain() {
    Promise.all([
      fetch(API.tle + '?group=visual').then(function (r) { return r.text(); }),
      fetch(API.tle + '?group=stations').then(function (r) { return r.text(); }),
    ]).then(function (texts) {
      var seen = {}, all = [];
      parseTLE(texts[0]).concat(parseTLE(texts[1])).forEach(function (s) { if (!seen[s.norad]) { seen[s.norad] = 1; all.push(s); } });
      sats = all;
      iss = sats.filter(function (s) { return /ISS|ZARYA|25544/.test(s.name + s.norad); })[0] || null;
      $('stCount').textContent = tr('sats_n').replace(':n', sats.length);
      var ld = $('stLoading'); if (ld) ld.classList.add('hidden');
      tick();
    }).catch(function () { ae('tool_error', { slug: 'satellite-tracker', where: 'tle' }); });
  }
  function toggleStarlink() {
    var btn = $('stStar');
    if (!showStar && !starlink.length) {
      btn.disabled = true;
      fetch(API.tle + '?group=starlink').then(function (r) { return r.text(); }).then(function (t) {
        starlink = parseTLE(t).slice(0, 1500); // cap for smooth rendering
        showStar = true; btn.classList.add('active'); btn.disabled = false; tick();
        ae('feature_use', { slug: 'satellite-tracker', feature: 'starlink_on' });
      }).catch(function () { btn.disabled = false; });
    } else { showStar = !showStar; btn.classList.toggle('active', showStar); draw(); }
  }
  function locate() {
    // A bare `return` here was a literally silent dead click: the button
    // did nothing and said nothing. __geo.notify explains why and reports it.
    if (!navigator.geolocation) { if (window.__geo) __geo.notify('no_api'); return; }
    var b = $('stLocate'); if (b) b.disabled = true;
    navigator.geolocation.getCurrentPosition(function (pos) {
      userLL = [pos.coords.latitude, pos.coords.longitude];
      map.setView(userLL, 3); LF.circleMarker(userLL, { radius: 6, color: '#7aa7ff', weight: 2, fillColor: '#7aa7ff', fillOpacity: .6 }).addTo(map);
      renderPasses(userLL[0], userLL[1]); ae('feature_use', { slug: 'satellite-tracker', feature: 'iss_passes' });
      if (b) b.disabled = false;
    }, function (err) { if (b) b.disabled = false; if (window.__geo) __geo.notify(err); }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 });
  }

  function boot() {
    try { window.Timbrica3D && window.Timbrica3D.init && window.Timbrica3D.init('satellite-tracker'); } catch (e) {}
    if (!window.satellite) { var ld = $('stLoading'); if (ld) ld.textContent = '⚠'; return; }
    initMap(); loadMain();
    var lb = $('stLocate'); if (lb) lb.addEventListener('click', locate);
    var sb = $('stStar'); if (sb) sb.addEventListener('click', toggleStarlink);
    var iv = setInterval(tick, 1000);
    ae('tool_complete', { slug: 'satellite-tracker' });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
